/* ==========================================================================
   IRON FIT - Fit Check (automatic)

   The customer uploads one photo. That's it. No lines to drag, no numbers to
   type, nothing else to touch.

   Finding the floor without asking:
     A photo carries no absolute size, so scale has to come from somewhere.
     We find the wall/floor junction by row-gradient energy - at the junction
     every column changes at once (wall above, floor below), so that row has
     far more horizontal edge energy than any other. The same scan over the
     top third finds the ceiling line when it's in frame.
     Ceiling height then defaults to 250 cm, which is a normal apartment, and
     the customer can correct it in one tap if theirs differs.

   Everything runs on the device. The photo is read with an object URL and is
   never uploaded anywhere.

   MACHINE geometry: the cut-out PNGs carry each machine's own soft floor
   shadow, so the SOLID frame bounds inside each file are stored below.
   Scaling off the raw image would draw every machine several cm too tall.
   ========================================================================== */
(function () {
  'use strict';

  var WA_NUMBER = '96181923182';

  var MACHINES = {
    compact:   { name: 'Compact Single-Stack Gym',      short: 'Compact',   model: 'assets/ifz-compact-3d.glb?v=4', lite: 'assets/ifz-compact-3d-lite.glb?v=1',   cmH: 226, cmW: 94,  cmD: 100 },
    crossover: { name: 'Dual Cable Crossover',          short: 'Crossover', model: 'assets/ifz-crossover-3d.glb?v=4', lite: 'assets/ifz-crossover-3d-lite.glb?v=1', cmH: 210, cmW: 150, cmD: 120 },
    smith:     { name: 'Multifunctional Smith Machine', short: 'Smith',     model: 'assets/ifz-smith-3d.glb?v=4', lite: 'assets/ifz-smith-3d-lite.glb?v=1',     cmH: 210, cmW: 150, cmD: 118 }
  };

  /* What the phone that took the photo probably saw. A modern phone's main
     camera is about 26 mm equivalent, which is a 65-70 degree vertical field
     in portrait; and people hold a phone at roughly chest height. Both are
     assumptions, but they are the assumptions that make a machine sit on the
     floor instead of floating, and they are close enough that nobody can tell
     from the result. */
  var PHOTO_FOV = 66;        /* degrees, vertical */
  var EYE_CM = 140;          /* how high the camera was off the floor */
  var MODEL_FOV = 30;        /* our own camera: long lens, little distortion */

  var $ = function (s) { return document.querySelector(s); };

  /* A package page links here as fit.html?m=crossover, so the check opens on
     the machine that is actually in the box the customer was looking at. */
  var urlM = (/[?&]m=([a-z-]+)/.exec(window.location.search) || [])[1];
  var photo = null, img = null, mkey = 'smith';
  var layer = null, mv = null, modelReady = false, turn = -20, envUrl = null;
  var viewerWanted = false, viewerLoading = false;
  var exposure = 1;
  var ceilingCm = 250;

  /* Guide positions are held as fractions of the SOURCE photo, not of the
     canvas. The canvas draws the photo with cover cropping, so a fraction of
     one is not a fraction of the other - mixing them drew the machine short
     and floating above the floor. cvY()/srcY() convert between the two. */
  var srcFloor = 0.82, srcCeil = 0.12;
  var posX = 0.5, depth = 1;
  var autoCeilFound = false;
  var cv, ctx, DPR = Math.min(2, window.devicePixelRatio || 1);
  var drag = null, adjusting = false;

  if (urlM && MACHINES[urlM]) mkey = urlM;

  function m() { return MACHINES[mkey]; }

  /* ==================================================================
     Auto-detect floor and ceiling from the photo
     ================================================================== */
  function detect(image) {
    /* 480 wide, not 220: at 220 each row covered ~5.5 source pixels, and that
       granularity alone left the floor line several centimetres out once
       scaled up. One 480x360 read is still trivial work. */
    var W = 480;
    var H = Math.max(60, Math.round(W * image.naturalHeight / image.naturalWidth));
    var c = document.createElement('canvas');
    c.width = W; c.height = H;
    var g = c.getContext('2d', { willReadFrequently: true });
    g.drawImage(image, 0, 0, W, H);
    var d = g.getImageData(0, 0, W, H).data;

    var gray = new Float32Array(W * H);
    for (var i = 0, p = 0; p < W * H; p++, i += 4) {
      gray[p] = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    }

    /* horizontal-edge energy per row: a wall/floor junction changes every
       column at once, so its row spikes far above ordinary clutter */
    var energy = new Float32Array(H);
    for (var y = 1; y < H - 1; y++) {
      var sum = 0;
      for (var x = 0; x < W; x++) {
        sum += Math.abs(gray[(y + 1) * W + x] - gray[(y - 1) * W + x]);
      }
      energy[y] = sum / W;
    }

    /* smooth so texture doesn't win over a real edge */
    var sm = new Float32Array(H);
    for (var y2 = 0; y2 < H; y2++) {
      var a = 0, n = 0;
      for (var k = -3; k <= 3; k++) {
        var yy = y2 + k;
        if (yy >= 0 && yy < H) { a += energy[yy]; n++; }
      }
      sm[y2] = a / n;
    }

    /* Smoothing turns a sharp junction into a short plateau, and taking the
       first maximum sits on its top edge - that alone put the floor ~4 rows
       high, which is several centimetres once scaled. Take the energy-weighted
       centroid of the plateau instead. */
    function peak(lo, hi) {
      var bi = -1, bv = -1;
      for (var y = Math.round(lo * H); y < Math.round(hi * H); y++) {
        if (sm[y] > bv) { bv = sm[y]; bi = y; }
      }
      if (bi < 0) return { y: (lo + hi) / 2, v: 0 };

      var floorLvl = bv * 0.55, num = 0, den = 0;
      for (var k = Math.max(0, bi - 7); k <= Math.min(H - 1, bi + 7); k++) {
        var w = sm[k] - floorLvl;
        if (w > 0) { num += k * w; den += w; }
      }
      var cy = den > 0 ? num / den : bi;
      return { y: cy / H, v: bv };
    }

    var mean = 0;
    for (var y3 = 0; y3 < H; y3++) mean += sm[y3];
    mean /= H;

    var f = peak(0.45, 0.95);
    var c2 = peak(0.02, 0.34);

    /* only trust a peak that clearly stands out from the average row */
    srcFloor = (f.v > mean * 1.35 && f.y > 0.45) ? f.y : 0.82;
    autoCeilFound = c2.v > mean * 1.5;
    srcCeil = autoCeilFound ? c2.y : 0.10;

    if (srcFloor - srcCeil < 0.25) { srcCeil = Math.max(0.04, srcFloor - 0.55); autoCeilFound = false; }
  }

  /* ---- source-space <-> canvas-space, through the cover transform ---- */
  function coverFit() {
    var s = Math.max(cv.width / photo.naturalWidth, cv.height / photo.naturalHeight);
    var dh = photo.naturalHeight * s;
    return { dh: dh, offY: (cv.height - dh) / 2 };
  }
  function cvY(srcFrac) {                    /* source fraction -> canvas fraction */
    if (!photo) return srcFrac;
    var f = coverFit();
    return (f.offY + srcFrac * f.dh) / cv.height;
  }
  function srcY(cvFrac) {                    /* canvas fraction -> source fraction */
    if (!photo) return cvFrac;
    var f = coverFit();
    return ((cvFrac * cv.height) - f.offY) / f.dh;
  }
  function floorY() { return cvY(srcFloor); }
  function ceilY()  { return cvY(srcCeil); }

  /* ==================================================================
     Canvas
     ================================================================== */
  function size() {
    var box = cv.parentNode.getBoundingClientRect();
    var w = box.width;
    var h = photo ? w * (photo.naturalHeight / photo.naturalWidth) : w * 0.6;
    h = Math.min(h, Math.max(300, window.innerHeight * 0.6));
    cv.style.width = w + 'px';
    cv.style.height = h + 'px';
    cv.width = Math.round(w * DPR);
    cv.height = Math.round(h * DPR);
    draw();
  }

  /* How many pixels one centimetre is.

     First choice: the wall. If we found both the floor and the ceiling in the
     photo, the distance between them IS the ceiling height, and that is the
     most reliable ruler a single photo can give.

     Fall-back: the floor alone. Plenty of room photos never show a ceiling —
     a garage shot from the driveway, anything wide. Then we stand on the
     geometry instead: the camera was EYE_CM off the floor, the horizon sits
     at eye level, and how far the floor point falls below the horizon tells
     us how far away that spot is. Distance gives scale. Guessing a ceiling
     that isn't in the picture drew the machine half a metre too tall and cut
     its head off. */
  /* the floor-only estimate: how far away that patch of floor is, from the
     camera's own height and how far below eye level it sits.

     "Below eye level" means below the HORIZON, not below the middle of the
     photograph. Those are the same thing only if the phone was held perfectly
     level, and it never is — people tilt down to get the floor in frame. This
     measured from the centre, which made every floor point read as further
     away than it was, which made a centimetre read as fewer pixels, which drew
     every machine SHORT: 71-86% of true size across the range of floor lines
     the detector actually produces.

     horizonFrac() is the same horizon the camera pitch already uses, so the
     two are now derived from one number instead of disagreeing. */
  function geomPxPerCm() {
    var t = Math.tan(PHOTO_FOV * Math.PI / 360);
    var below = (floorY() - horizonFrac()) * 2 * t;
    var dist = EYE_CM / Math.max(0.06, below);
    return cv.height / (2 * dist * t);
  }

  /* px per cm.

     Two cases, and they are no longer pretending to be independent:

     CEILING FOUND — the wall is a real ruler. Floor line to ceiling line IS
     ceilingCm, and nothing beats measuring the thing you can see.

     NO CEILING — fall back to camera geometry. Note that once geomPxPerCm()
     measures from the horizon rather than the frame centre, and the horizon
     is itself derived from the floor and ceiling lines, the two expressions
     reduce to exactly the same number when a ceiling IS found. The old code
     cross-checked them and rejected the wall if they disagreed by more than
     half — that check could only ever fire because the geometry estimate was
     biased ~25% low. With the bias gone the ratio is identically 1.0, so the
     check is dead weight and is gone with it. */
  function pxPerCm() {
    if (autoCeilFound) return (floorY() - ceilY()) * cv.height / ceilingCm;
    return geomPxPerCm();
  }

  /* The machine's height on screen, in CSS pixels. Everything else follows
     from this one number — it is what "true size" means here. */
  function machinePxCss() { return m().cmH * pxPerCm() * depth / DPR; }

  /* Where the horizon is.

     A photo has no horizon line drawn on it, but we know two things about
     this one: where the floor meets the wall, and where the ceiling does.
     The camera was EYE_CM off the floor, so the eye-level line falls between
     those two in exactly that proportion. That gives us the horizon without
     asking the customer anything, and the horizon is what tells us how far
     the phone was tilted down. */
  function horizonFrac() {
    var f = floorY(), c = ceilY();
    return c + (f - c) * (ceilingCm - EYE_CM) / ceilingCm;
  }

  /* How far the camera was tilted, in degrees. Positive means tilted down. */
  function pitchDeg() {
    var t = Math.tan(PHOTO_FOV * Math.PI / 360);
    var off = (0.5 - horizonFrac()) * 2 * t;          /* horizon above centre = tilted down */
    return Math.max(-24, Math.min(34, Math.atan(off) * 180 / Math.PI));
  }

  /* Put the 3D layer over the photo: right size, right place, right tilt. */
  function placeModel() {
    if (!mv || !modelReady) return;

    var stage = cv.getBoundingClientRect();
    var W = stage.width, H = stage.height;

    layer.style.width = W + 'px';
    layer.style.height = H + 'px';

    var hPx = machinePxCss();

    /* Pull the camera back until the machine projects to exactly hPx tall.
       projected = viewportH * modelHeight / (2 * distance * tan(fov/2)) */
    var dims = mv.getDimensions();
    var dist = dims.y * H / (2 * hPx * Math.tan(MODEL_FOV * Math.PI / 360));

    mv.setAttribute('field-of-view', MODEL_FOV + 'deg');
    mv.setAttribute('camera-orbit', turn + 'deg ' + (90 - pitchDeg()) + 'deg ' + dist + 'm');

    /* The model renders in the middle of its own box, so the box moves to put
       the machine's feet on the floor line at the chosen spot. */
    var cx = posX * W;
    var cy = floorY() * H - hPx / 2;
    layer.style.transform = 'translate(' + (cx - W / 2) + 'px,' + (cy - H / 2) + 'px)';
  }

  function draw() {
    if (!ctx) return;
    var W = cv.width, H = cv.height;
    ctx.clearRect(0, 0, W, H);
    if (!photo) { ctx.fillStyle = '#0b0f0b'; ctx.fillRect(0, 0, W, H); return; }

    var s = Math.max(W / photo.naturalWidth, H / photo.naturalHeight);
    var dw = photo.naturalWidth * s, dh = photo.naturalHeight * s;
    ctx.drawImage(photo, (W - dw) / 2, (H - dh) / 2, dw, dh);

    placeModel();
    if (adjusting) guides();
  }

  function guides() {
    var W = cv.width, H = cv.height, fy = floorY() * H, cy = ceilY() * H;
    ctx.save();
    ctx.lineWidth = 2 * DPR;
    ctx.setLineDash([10 * DPR, 8 * DPR]);
    ctx.strokeStyle = '#35e02a';
    ctx.beginPath(); ctx.moveTo(0, fy); ctx.lineTo(W, fy); ctx.stroke();
    ctx.strokeStyle = '#7ad4ff';
    ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(W, cy); ctx.stroke();
    ctx.setLineDash([]);
    [[fy, '#35e02a', 'FLOOR'], [cy, '#7ad4ff', 'CEILING']].forEach(function (r) {
      ctx.beginPath();
      ctx.arc(W - 26 * DPR, r[0], 10 * DPR, 0, Math.PI * 2);
      ctx.fillStyle = r[1]; ctx.fill();
      ctx.strokeStyle = 'rgba(5,7,5,.85)'; ctx.lineWidth = 2 * DPR; ctx.stroke();
      ctx.fillStyle = r[1];
      ctx.font = '700 ' + (12 * DPR) + 'px Inter, Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(r[2], 12 * DPR, r[0] - 9 * DPR);
    });
    ctx.restore();
  }

  /* ==================================================================
     Verdict
     ================================================================== */
  function verdict() {
    var el = $('#verdict');
    if (!photo) { el.hidden = true; return; }
    el.hidden = false;

    var mm = m(), clear = ceilingCm - mm.cmH, ok = clear >= 5;

    /* When it doesn't fit, name the shortest machine that actually would,
       rather than a hard-coded one — the Compact is now the tallest of the
       three, so the old "the Compact is 205 cm" line pointed the wrong way. */
    var alt = null;
    Object.keys(MACHINES).forEach(function (k) {
      var c = MACHINES[k];
      if (c === mm || ceilingCm - c.cmH < 5) return;
      if (!alt || c.cmH < alt.cmH) alt = c;
    });

    el.className = 'fit__verdict ' + (ok ? 'is-ok' : 'is-bad');
    el.innerHTML =
      '<span class="fit__mark">' + (ok ? '&#10003;' : '&#10007;') + '</span>' +
      '<span class="fit__vtx">' +
        '<b>' + (ok ? 'It fits.' : 'Too tight.') + '</b> ' +
        (ok
          ? 'The ' + mm.name + ' is <b>' + mm.cmH + ' cm</b> tall, your ceiling is <b>' + ceilingCm +
            ' cm</b> &mdash; <b>' + clear + ' cm</b> to spare. On the floor it takes <b>' +
            mm.cmW + ' &times; ' + mm.cmD + ' cm</b>.'
          : 'The ' + mm.name + ' is <b>' + mm.cmH + ' cm</b> tall but your ceiling is <b>' + ceilingCm +
            ' cm</b>. You need <b>' + (mm.cmH + 5) + ' cm</b>.' +
            (alt ? ' The ' + alt.name + ' is <b>' + alt.cmH + ' cm</b> &mdash; that one fits.'
                 : ' Message us and we will find you something that fits.')) +
      '</span>';

    $('#fitWa').href = 'https://wa.me/' + WA_NUMBER + '?text=' +
      encodeURIComponent('Hi! I checked the fit at home. Ceiling: ' + ceilingCm + ' cm. ' +
                         'I am interested in the ' + mm.name + ' (' + mm.cmH + ' cm tall). ');

    $('#ceilRead').textContent = ceilingCm + ' cm';
    $('#autoNote').innerHTML = autoCeilFound
      ? 'Floor and ceiling found automatically.'
      : 'Floor found automatically. Ceiling assumed to be <b>250 cm</b> &mdash; tap to change.';
  }

  /* ==================================================================
     Input
     ================================================================== */
  function loadPhoto(file) {
    if (!file) return;
    $('#fitStage').classList.add('is-busy');
    var url = URL.createObjectURL(file);
    var im = new Image();
    im.onload = function () {
      photo = im;
      detect(im);
      posX = 0.5; depth = 1;
      lightFromRoom(im, url);           /* keeps `url` alive as the env map */
      wantViewer();
      $('#fitStage').classList.remove('is-busy');
      $('#fitStage').classList.add('has-photo');
      document.body.classList.add('fit-ready');
      /* now there is something to gesture on, so say what the gestures are */
      var g = $('#fitGest');
      if (g) {
        g.hidden = false;
        /* it has done its job after a few seconds and is then just covering
           the bottom of the customer's own photograph */
        setTimeout(function () { g.hidden = true; }, 6000);
      }
      size(); verdict();
    };
    im.onerror = function () {
      $('#fitStage').classList.remove('is-busy');
      alert('That file could not be opened. Try another photo.');
    };
    im.src = url;
  }

  /* Build the viewer once, then just swap the model when the customer picks a
     different machine. */
  /* Pull in the viewer the first time it is actually needed. */
  function wantViewer() {
    viewerWanted = true;
    if (viewerLoading || window.customElements && customElements.get('model-viewer')) {
      loadMachine();
      return;
    }
    viewerLoading = true;

    /* The machines are full-resolution meshopt-compressed meshes now, and
       model-viewer ships no default location for the meshopt decoder — set
       it before the viewer script runs or the model never parses. Local
       copy, so the fit check keeps working with no internet. */
    window.ModelViewerElement = window.ModelViewerElement || {};
    if (!window.ModelViewerElement.meshoptDecoderLocation) {
      window.ModelViewerElement.meshoptDecoderLocation = window.IFZA('assets/meshopt_decoder.js');
      /* The models are Draco now — half the download of meshopt for the
         same 1.9M triangles. Vendored, so nothing is fetched from gstatic. */
      window.ModelViewerElement.dracoDecoderLocation = window.IFZA('assets/');
    }

    var sc = document.createElement('script');
    sc.type = 'module';
    sc.src = window.IFZA('assets/model-viewer.min.js');
    sc.onload = function () { loadMachine(); };
    document.head.appendChild(sc);
  }

  /* ------------------------------------------------------------------
     WARM-UP. The model is ~6 MB and nothing used to touch it until a
     photo had been chosen, so the whole download landed on top of the
     one moment the page has to feel instant.

     This page hands us a gift the machine page does not: the visitor
     picks a machine, then goes off to photograph a room. That is easily
     enough time to have the file already on the phone. Fetched here and
     read from cache by model-viewer later — same URL, same entry, which
     is why both go through IFZA.

     Held back on Save-Data and 2G, and only ever the machine actually
     selected: prefetching all three would be 17 MB nobody asked for.
     ------------------------------------------------------------------ */
  var warmedUrls = {};

  function warmModel(k) {
    var m = MACHINES[k];
    if (!m || !window.fetch) return;
    var c = navigator.connection;
    if (c && (c.saveData || /2g/.test(c.effectiveType || ''))) return;

    function grab(url) {
      if (warmedUrls[url]) return warmedUrls[url];
      /* drained so it is actually written to cache, then dropped — the
         final then() returns nothing on purpose, or warmedUrls would keep
         several MB alive for the life of the page */
      warmedUrls[url] = fetch(url)
        .then(function (r) { return r.arrayBuffer(); })
        .then(function () {})
        .catch(function () {});
      return warmedUrls[url];
    }

    grab(window.IFZA('assets/draco_wasm_wrapper.js'));
    grab(window.IFZA('assets/draco_decoder.wasm'));
    /* light model first and on its own; the full mesh only once it is
       down, so the two are never fighting over the same connection */
    grab(window.IFZA(m.lite || m.model)).then(function () {
      if (m.lite) grab(window.IFZA(m.model));
    });
  }

  function loadMachine() {
    if (!layer || !viewerWanted) return;
    modelReady = false;

    if (!mv) {
      mv = document.createElement('model-viewer');
      mv.setAttribute('interaction-prompt', 'none');
      mv.setAttribute('disable-zoom', '');
      mv.setAttribute('disable-tap', '');
      mv.setAttribute('camera-controls', '');
      mv.setAttribute('shadow-intensity', '1.25');   /* what stops it floating */
      mv.setAttribute('shadow-softness', '0.9');
      mv.setAttribute('loading', 'eager');
      mv.style.width = '100%';
      mv.style.height = '100%';
      mv.style.setProperty('--poster-color', 'transparent');
      mv.addEventListener('load', function () {
        modelReady = true;
        layer.hidden = false;
        placeModel();
        verdict();
        /* Only now — with a machine actually in the photo — is it safe to
           pull the full mesh down. Started any earlier it would be racing
           the light model for the connection. A blob src means this IS the
           full mesh arriving, so there is nothing left to upgrade to. */
        if (String(mv.src).indexOf('blob:') !== 0) upgradeMachine(mkey);
      });
      layer.appendChild(mv);
    }

    layer.hidden = true;
    /* Through IFZA. On Shopify the page is /pages/fit-check and a bare
       'assets/…' resolves to /pages/assets/… — a 404, and no machine.
       The spin viewer already went through IFZA; this one did not. */
    mv.setAttribute('src', window.IFZA(m().lite || m().model));
    if (envUrl) mv.setAttribute('environment-image', envUrl);
    mv.setAttribute('exposure', String(exposure));
  }

  /* The light model gets the machine into the photo in about two seconds;
     the full mesh replaces it once it has arrived. Same two-stage load as
     the machine pages, and the same reason — see ifz-3d.js. Here it matters
     more, because the answer to "will it fit" should not need a download. */
  var upgradeToken = 0;

  function upgradeMachine(key) {
    var mine = ++upgradeToken;
    var spec = MACHINES[key];
    if (!spec || !spec.lite || !window.fetch) return;

    fetch(window.IFZA(spec.model)).then(function (r) {
      if (!r.ok) throw new Error('http ' + r.status);
      return r.blob();
    }).then(function (blob) {
      /* they switched machine, or picked a new photo, while this was in
         flight — dropping it is the whole point of the token */
      if (mine !== upgradeToken || !mv) return;
      var url = URL.createObjectURL(blob);
      mv.addEventListener('load', function once() {
        mv.removeEventListener('load', once);
        URL.revokeObjectURL(url);
      });
      mv.src = url;
    }).catch(function () {});
  }

  /* The room lights the machine.

     Handing model-viewer the customer's own photo as the environment map is
     not physically right — a flat photo is not a 360 panorama — but it is
     what makes the steel pick up the colour of that particular room instead
     of a studio grey, and a warm room stops looking like a showroom paste-up.
     The exposure comes from how bright the photo actually is, so a machine
     dropped into a dim garage is dim too. */
  function lightFromRoom(image, url) {
    envUrl = url;

    var c = document.createElement('canvas');
    c.width = 64; c.height = 64;
    var g = c.getContext('2d', { willReadFrequently: true });
    g.drawImage(image, 0, 0, 64, 64);
    var px = g.getImageData(0, 0, 64, 64).data;
    var sum = 0;
    for (var i = 0; i < px.length; i += 4) {
      sum += 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
    }
    var mean = sum / (px.length / 4) / 255;          /* 0 dark … 1 bright */
    exposure = Math.max(0.55, Math.min(1.5, 0.45 + mean * 1.35));

    if (mv) {
      mv.setAttribute('environment-image', envUrl);
      mv.setAttribute('exposure', String(exposure));
    }
  }

  function pt(e) {
    var r = cv.getBoundingClientRect();
    var t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) / r.width, y: (t.clientY - r.top) / r.height };
  }

  /* One finger turns the machine.

     Dragging it sideways used to be the only gesture here, but a 3D object
     on a phone is expected to spin when you drag it — that is what everyone
     tries first, and it did nothing. Moving it is now the two-finger
     gesture, which is also the convention, and the Adjust panel still has
     sliders for anyone who would rather be exact.

     The guide lines keep the single finger while the Adjust panel is open,
     because that is the only way to correct a floor line we got wrong. */
  var lastX = 0;

  function onDown(e) {
    if (!photo || touching) return;
    var p = pt(e);
    if (adjusting && Math.abs(p.y - floorY()) < 0.04) drag = 'floor';
    else if (adjusting && Math.abs(p.y - ceilY()) < 0.04) drag = 'ceil';
    else drag = 'turn';
    lastX = p.x;
    e.preventDefault();
  }
  function onMove(e) {
    if (!drag || !photo || touching) return;
    var p = pt(e);
    if (drag === 'floor') srcFloor = Math.min(1.2, Math.max(srcCeil + 0.1, srcY(p.y)));
    else if (drag === 'ceil') srcCeil = Math.max(-0.2, Math.min(srcFloor - 0.1, srcY(p.y)));
    else { spinBy((p.x - lastX) * 320); lastX = p.x; }
    draw();
    if (drag !== 'turn') verdict();
    e.preventDefault();
  }
  function onUp() { drag = null; }

  /* ------------------------------------------------------------------
     TOUCH: turn with one finger, pinch to change the size, two fingers
     to slide it along the floor.

     "Bigger and smaller" is the DEPTH control, not a free scale. The whole
     point of this page is that the machine is drawn at its true size in
     your room, so letting somebody resize it at will would quietly turn
     the answer into a lie. Pinching stands it nearer or further back
     instead, which changes how big it looks for an honest reason.
     ------------------------------------------------------------------ */
  var touching = false, t0 = null;

  function spinBy(deg) {
    turn = ((turn + deg + 180) % 360 + 360) % 360 - 180;
    var s = $('#fitTurn'); if (s) s.value = Math.round(turn);
  }
  function depthTo(v) {
    depth = Math.max(0.60, Math.min(1.40, v));
    var s = $('#fitDepth'); if (s) s.value = Math.round(depth * 100);
  }

  function touchInfo(e) {
    var a = e.touches[0], b = e.touches[1];
    var dx = b.clientX - a.clientX, dy = b.clientY - a.clientY;
    return {
      dist: Math.sqrt(dx * dx + dy * dy),
      ang: Math.atan2(dy, dx) * 180 / Math.PI,
      cx: (a.clientX + b.clientX) / 2
    };
  }

  function bindTouch() {
    cv.addEventListener('touchstart', function (e) {
      if (!photo || live) return;
      if (e.touches.length === 2) {
        touching = true; drag = null;
        var i = touchInfo(e);
        t0 = { dist: i.dist, ang: i.ang, cx: i.cx, depth: depth, turn: turn, posX: posX };
      } else if (e.touches.length === 1 && !touching) {
        lastX = pt(e).x;
      }
    }, { passive: true });

    cv.addEventListener('touchmove', function (e) {
      if (!photo || live) return;

      if (e.touches.length === 2 && t0) {
        e.preventDefault();
        var i = touchInfo(e), b = cv.getBoundingClientRect();
        if (t0.dist > 0) depthTo(t0.depth * (i.dist / t0.dist));
        /* twisting two fingers turns it, the same way you would turn a
           real object you had hold of with both hands */
        var d = i.ang - t0.ang;
        if (d > 180) d -= 360; if (d < -180) d += 360;
        turn = t0.turn + d;
        var s = $('#fitTurn'); if (s) s.value = Math.round(turn);
        posX = Math.min(1, Math.max(0, t0.posX + (i.cx - t0.cx) / b.width));
        draw();
        return;
      }

      /* One finger is deliberately NOT handled here. A touch fires
         pointermove as well as touchmove, and onMove already turns the
         machine — doing it in both places spins it at double speed. The
         pointer path owns one finger; this owns two. */
    }, { passive: false });

    function end(e) {
      if (!e.touches || e.touches.length === 0) { touching = false; t0 = null; drag = null; }
    }
    cv.addEventListener('touchend', end, { passive: true });
    cv.addEventListener('touchcancel', end, { passive: true });
  }

  /* ==================================================================
     Save
     ================================================================== */
  function save() {
    var out = document.createElement('canvas');
    out.width = cv.width; out.height = cv.height;
    var o = out.getContext('2d');
    var W = cv.width, H = cv.height;
    var s = Math.max(W / photo.naturalWidth, H / photo.naturalHeight);
    var dw = photo.naturalWidth * s, dh = photo.naturalHeight * s;
    o.drawImage(photo, (W - dw) / 2, (H - dh) / 2, dw, dh);

    /* The machine is rendered by the 3D viewer, in its own canvas, offset
       over the photo — so the saved picture has to composite the two. */
    if (mv && modelReady) {
      try {
        var shot = new Image();
        shot.src = mv.toDataURL('image/png');
        var box = layer.getBoundingClientRect();
        var st = cv.getBoundingClientRect();
        o.drawImage(shot, (box.left - st.left) * DPR, (box.top - st.top) * DPR,
                          box.width * DPR, box.height * DPR);
      } catch (e) { /* nothing to composite */ }
    }
    o.fillStyle = 'rgba(5,7,5,.78)';
    o.fillRect(0, H - 44 * DPR, W, 44 * DPR);
    o.fillStyle = '#f6faf6';
    o.font = '700 ' + (14 * DPR) + 'px Inter, Arial, sans-serif';
    o.textAlign = 'left';
    o.fillText('IRON FIT   ' + m().name + '   ' + m().cmH + ' cm tall', 14 * DPR, H - 16 * DPR);
    out.toBlob(function (blob) {
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'ironfit-fit-check.png';
      a.click();
      setTimeout(function () { URL.revokeObjectURL(a.href); }, 2000);
    }, 'image/png');
  }

  /* ==================================================================
     LIVE VIEW

     The still-photo path has to guess where the horizon is, because a JPEG
     does not say how the phone was tilted. A live view does: the gyroscope
     reports it, so the scale here rests on a measurement instead of an
     assumption. That is what makes the machine trustworthy at real size,
     and it is what lets us say "step back 1.2 m" before the shot is taken
     rather than after.

     The geometry, once:
       horizon      the frame row where a ray leaves the lens horizontally.
                    Pitch the phone down by t and it rises by
                    tan(t) / (2 tan(fov/2)) of the frame.
       below        how far the machine's feet sit below that horizon,
                    as a tangent.
       distance     EYE_CM / below  — the camera is EYE_CM off the floor,
                    so a floor point's angle below level IS its distance.
       scale        pixels per cm at that distance.

     None of it needs the room to be measured, and none of it needs a
     ceiling to be in shot.
     ================================================================== */

  var live = false, liveStream = null, liveRaf = 0;
  var livePitch = 22;          /* degrees below level; sane default until the
                                  gyroscope reports, so the first frame is not
                                  wildly wrong on a device that denies motion */
  var liveGyro = false;
  var liveFloor = 0.80;        /* where the feet sit in frame — draggable */
  var liveX = 0.5;

  function liveSupported() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }

  function onOrient(e) {
    /* beta is the front-back tilt: 90 = held upright facing forward,
       0 = flat on its back. Pointing a camera at a room across the floor
       is somewhere in between. */
    if (e.beta == null) return;
    liveGyro = true;
    livePitch = Math.max(-20, Math.min(70, 90 - e.beta));
  }

  /* horizon as a fraction of the frame, from the measured pitch */
  function liveHorizon() {
    var t = Math.tan(PHOTO_FOV * Math.PI / 360);
    return 0.5 - Math.tan(livePitch * Math.PI / 180) / (2 * t);
  }

  /* ground distance, in cm, to the point the feet are standing on */
  function liveDistanceCm() {
    var t = Math.tan(PHOTO_FOV * Math.PI / 360);
    var below = (liveFloor - liveHorizon()) * 2 * t;
    if (below <= 0.02) return Infinity;          /* aimed at or above level */
    return EYE_CM / below;
  }

  /* How far back you must stand for the whole machine to be in frame.
     The machine fills  cmH / (2 d tan(fov/2))  of the frame, so for it to
     fill at most `fill`, d must be at least cmH / (2 fill tan(fov/2)). */
  function liveNeedCm(fill) {
    var t = Math.tan(PHOTO_FOV * Math.PI / 360);
    return m().cmH / (2 * fill * t);
  }

  function coach(msg, sub, tone) {
    var el = $('#fitCoach');
    if (!el) return;
    el.hidden = false;
    el.className = 'fit__coach' + (tone ? ' is-' + tone : '');
    el.querySelector('b').textContent = msg;
    el.querySelector('small').textContent = sub || '';
  }

  function liveTick() {
    if (!live) return;
    liveRaf = requestAnimationFrame(liveTick);
    if (!mv || !modelReady || !layer) return;

    var stage = $('#fitStage').getBoundingClientRect();
    var W = stage.width, H = stage.height;
    if (!W || !H) return;

    layer.style.width = W + 'px';
    layer.style.height = H + 'px';

    var d = liveDistanceCm();

    /* Aimed too high to see any floor — nothing to stand the machine on. */
    if (!isFinite(d)) {
      coach('Tilt the phone down', 'Point at the floor where the machine would stand', 'warn');
      layer.style.opacity = '.25';
      return;
    }
    layer.style.opacity = '1';

    var t = Math.tan(PHOTO_FOV * Math.PI / 360);
    var pxPerCmLive = H / (2 * d * t);
    var hPx = m().cmH * pxPerCmLive;

    /* THE BACK-UP PROMPT.
       If the machine cannot fit in the frame from here, say so in metres —
       "step back" is useless without a number. */
    var fills = hPx / H;
    if (fills > 0.92) {
      var need = liveNeedCm(0.78);
      var back = Math.max(0, need - d);
      coach('Step back about ' + (back / 100).toFixed(1) + ' m',
            'The ' + m().cmH + ' cm machine will not fit on screen from here', 'warn');
    } else if (fills < 0.25) {
      coach('Come closer, or aim lower',
            'The machine is tiny from this far — move in for a real look', 'warn');
    } else {
      coach('This is real size',
            m().cmH + ' cm tall, ' + Math.round(d / 10) / 10 + ' m away · drag to move it', 'ok');
    }

    /* Same projection the photo path uses: pull the camera back until the
       model measures exactly hPx on screen. */
    var dims = mv.getDimensions();
    var dist = dims.y * H / (2 * hPx * Math.tan(MODEL_FOV * Math.PI / 360));
    mv.setAttribute('field-of-view', MODEL_FOV + 'deg');
    mv.setAttribute('camera-orbit', turn + 'deg ' + (90 - livePitch) + 'deg ' + dist + 'm');

    var cx = liveX * W;
    var cy = liveFloor * H - hPx / 2;
    layer.style.transform = 'translate(' + (cx - W / 2) + 'px,' + (cy - H / 2) + 'px)';
  }

  function startLive() {
    if (!liveSupported()) {
      coach('This browser will not open the camera',
            'Use "Choose photo" instead — it does the same measurement', 'warn');
      return;
    }

    /* iOS only hands over the gyroscope from inside a user gesture, which is
       the tap that got us here. Denied is survivable — livePitch keeps its
       default and the machine is placed from the drag alone. */
    try {
      var DOE = window.DeviceOrientationEvent;
      if (DOE && typeof DOE.requestPermission === 'function') {
        DOE.requestPermission().then(function (s) {
          if (s === 'granted') window.addEventListener('deviceorientation', onOrient);
        }).catch(function () {});
      } else if (DOE) {
        window.addEventListener('deviceorientation', onOrient);
      }
    } catch (e) {}

    navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } }, audio: false
    }).then(function (stream) {
      liveStream = stream;
      live = true;
      var v = $('#fitVideo');
      v.srcObject = stream;
      v.hidden = false;
      v.play().catch(function () {});
      $('#fitStage').classList.add('is-live');
      $('#fitLiveBar').hidden = false;
      $('#fitLiveBtn').hidden = true;
      layer.hidden = false;
      coach('Starting the camera…', '', '');
      wantViewer();
      cancelAnimationFrame(liveRaf);
      liveTick();
    }).catch(function (err) {
      var secure = window.isSecureContext;
      coach(secure ? 'Camera blocked' : 'Camera needs a secure connection',
            secure ? 'Allow camera access, or use "Choose photo" instead'
                   : 'Live view only works over https. "Choose photo" works anywhere.',
            'warn');
    });
  }

  function stopLive() {
    live = false;
    cancelAnimationFrame(liveRaf);
    if (liveStream) { liveStream.getTracks().forEach(function (t) { t.stop(); }); liveStream = null; }
    var v = $('#fitVideo');
    if (v) { v.srcObject = null; v.hidden = true; }
    $('#fitStage').classList.remove('is-live');
    $('#fitLiveBar').hidden = true;
    $('#fitLiveBtn').hidden = !liveSupported();
    $('#fitCoach').hidden = true;
    window.removeEventListener('deviceorientation', onOrient);
  }

  /* Freeze: grab the current video frame and hand it to the photo path, so
     the verdict, the save and the WhatsApp message all work exactly as they
     already do. The live view is a way of AIMING, not a second product. */
  function freezeLive() {
    var v = $('#fitVideo');
    if (!v || !v.videoWidth) return;
    var c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob(function (b) {
      if (!b) return;
      var f = new File([b], 'room.jpg', { type: 'image/jpeg' });
      stopLive();
      loadPhoto(f);
    }, 'image/jpeg', 0.92);
  }

  /* ==================================================================
     Wire up
     ================================================================== */
  function init() {
    cv = $('#fitCanvas');
    layer = $('#fitModel');

    /* the same viewer the machine pages use, already vendored */
    /* The viewer and the model are 6 MB between them. Nothing is fetched
       until there is a photo to stand the machine in — most people who open
       this page are only having a look. */
    viewerWanted = false;
    if (!cv) return;
    ctx = cv.getContext('2d');

    var pre = new URLSearchParams(location.search).get('m');
    if (MACHINES[pre]) mkey = pre;

    var pick = $('#fitPick');
    Object.keys(MACHINES).forEach(function (k) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'fit__pick' + (k === mkey ? ' is-on' : '');
      b.innerHTML = MACHINES[k].short + '<small>' + MACHINES[k].cmH + ' cm</small>';
      b.addEventListener('click', function () {
        mkey = k;
        [].forEach.call(pick.children, function (c) { c.classList.remove('is-on'); });
        b.classList.add('is-on');
        warmModel(k);
        if (photo) wantViewer(); else loadMachine();
      });
      pick.appendChild(b);
    });

    /* whichever machine is showing when the page opens — the download runs
       while they are still reading the page and photographing the room */
    if (document.readyState === 'complete') setTimeout(function () { warmModel(mkey); }, 800);
    else window.addEventListener('load', function () { setTimeout(function () { warmModel(mkey); }, 800); });

    $('#fitPhoto').addEventListener('change', function (e) { loadPhoto(e.target.files[0]); });
    $('#fitRedo').addEventListener('click', function () { stopLive(); $('#fitPhoto').click(); });

    /* ---------------- live view ---------------- */
    var liveBtn = $('#fitLiveBtn');
    if (liveBtn) {
      liveBtn.hidden = !liveSupported();
      liveBtn.addEventListener('click', startLive);
    }
    var stopBtn = $('#fitLiveStop');
    if (stopBtn) stopBtn.addEventListener('click', stopLive);
    var freezeBtn = $('#fitFreeze');
    if (freezeBtn) freezeBtn.addEventListener('click', freezeLive);
    var liveTurn = $('#fitLiveTurn');
    if (liveTurn) liveTurn.addEventListener('input', function () { turn = +liveTurn.value; });

    /* Drag inside the live view: sideways slides the machine along the wall,
       up and down walks it further away or nearer. Both are just moving the
       point its feet stand on, which is the only thing the scale depends on. */
    (function () {
      var stage = $('#fitStage');
      if (!stage) return;
      var from = null;
      function pt(e) { var t = e.touches ? e.touches[0] : e; return { x: t.clientX, y: t.clientY }; }

      stage.addEventListener('touchstart', function (e) {
        if (!live) return;
        from = pt(e);
      }, { passive: true });

      stage.addEventListener('touchmove', function (e) {
        if (!live || !from) return;
        e.preventDefault();
        var p = pt(e), b = stage.getBoundingClientRect();
        liveX = Math.max(.08, Math.min(.92, liveX + (p.x - from.x) / b.width));
        liveFloor = Math.max(.35, Math.min(.97, liveFloor + (p.y - from.y) / b.height));
        from = p;
      }, { passive: false });

      stage.addEventListener('touchend', function () { from = null; }, { passive: true });

      /* mouse, so it can be tried on a laptop too */
      stage.addEventListener('mousedown', function (e) { if (live) from = pt(e); });
      window.addEventListener('mousemove', function (e) {
        if (!live || !from) return;
        var p = pt(e), b = stage.getBoundingClientRect();
        liveX = Math.max(.08, Math.min(.92, liveX + (p.x - from.x) / b.width));
        liveFloor = Math.max(.35, Math.min(.97, liveFloor + (p.y - from.y) / b.height));
        from = p;
      });
      window.addEventListener('mouseup', function () { from = null; });
    })();

    /* ceiling height: preset chips, no keyboard needed */
    var chips = $('#ceilChips');
    [230, 240, 250, 260, 270, 300].forEach(function (v) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'fit__chip' + (v === ceilingCm ? ' is-on' : '');
      b.textContent = v;
      b.addEventListener('click', function () {
        ceilingCm = v;
        [].forEach.call(chips.children, function (c) { c.classList.remove('is-on'); });
        b.classList.add('is-on');
        autoCeilFound = true;
        draw(); verdict();
      });
      chips.appendChild(b);
    });

    var adj = $('#fitAdjust'), panel = $('#adjustPanel');
    adj.addEventListener('click', function () {
      adjusting = !adjusting;
      panel.hidden = !adjusting;
      adj.classList.toggle('is-on', adjusting);
      adj.setAttribute('aria-expanded', adjusting ? 'true' : 'false');
      draw();
    });

    $('#fitTurn').addEventListener('input', function (e) {
      turn = +e.target.value;
      placeModel();
    });

    $('#fitDepth').addEventListener('input', function (e) {
      depth = parseInt(e.target.value, 10) / 100; draw();
    });

    cv.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    bindTouch();

    $('#fitSave').addEventListener('click', function () { if (photo) save(); });

    /* Both, not either: resize covers orientation changes, and the observer
       catches layout shifts that raise no resize event at all. */
    window.addEventListener('resize', size);
    window.addEventListener('orientationchange', size);
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(function () { size(); }).observe(cv.parentNode);
    }

    loadMachine(); size(); verdict();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
